import { trackControl } from "@/lib/analytics";
import { actionProps, scopeProps, type ControlContext } from "@/lib/controlAnalytics";
import type { ReactNode } from "react";
import { Dialog, DialogDescription, DialogTitle } from "@jobenvoy/ui";
import { CloseIcon } from "@/components/icons";

/**
 * The dialog shell, as the design draws it: 20 px corners, the dialog
 * shadow, a scrim of the text colour at 42% with a 5 px blur behind. The
 * header carries the title, a line under it and the close; the footer
 * sits on the raised card with the actions on the right, the one
 * gradient button last. On a phone it fills the screen, with the
 * footer at its foot; `compact` is for a confirmation, which stays a
 * card in the middle of the screen at every width.
 */
export default function Modal({
    open,
    analyticsContext = { source: "Modal" },
    onClose,
    title,
    description,
    width = 480,
    compact = false,
    children,
    footer,
}: {
    open: boolean;
    analyticsContext?: ControlContext;
    onClose: () => void;
    title: ReactNode;
    description?: ReactNode;
    width?: number;
    compact?: boolean;
    children?: ReactNode;
    footer?: ReactNode;
}) {
    return (
        <Dialog
            {...scopeProps(analyticsContext)}
            open={open}
            onClose={() => {
                // Headless UI still hears Escape while the dialog fades out:
                // a held key would close it, and count the dismissal, twice.
                if (!open) return;
                trackControl("dialog_dismissed", { ...analyticsContext });
                onClose();
            }}
            className={
                compact
                    ? "overflow-hidden rounded-[20px] border p-0 shadow-dialog"
                    : "overflow-hidden rounded-none border-0 p-0 shadow-dialog max-[760px]:min-h-dvh max-[760px]:!max-w-none min-[761px]:rounded-[20px] min-[761px]:border"
            }
            backdropClassName="bg-scrim backdrop-blur-[5px]"
            // `sm:` sorts after an arbitrary breakpoint, so the wide frame takes `!`.
            frameClassName={
                compact ? "p-4 sm:p-4 min-[761px]:!p-10" : "p-0 sm:p-0 min-[761px]:!p-10"
            }
            rowClassName={
                compact
                    ? "items-center sm:items-center"
                    : "items-stretch sm:items-stretch min-[761px]:!items-center"
            }
            panelStyle={{ maxWidth: width }}
        >
            <div
                className={`flex w-full flex-col overflow-hidden ${compact ? "" : "max-[760px]:min-h-dvh"}`}
            >
                <div
                    className={`flex items-start justify-between gap-4 px-7 pt-6 ${children ? "" : "pb-6"}`}
                >
                    <div className="flex min-w-0 flex-col gap-1.5">
                        <DialogTitle className="m-0 font-display text-[22px] font-bold leading-[1.15] tracking-[-0.02em] sm:text-[22px]">
                            {title}
                        </DialogTitle>
                        {description && (
                            <DialogDescription className="m-0 text-[13px] leading-normal text-fg-muted">
                                {description}
                            </DialogDescription>
                        )}
                    </div>
                    <button
                        {...actionProps("dialog_close_clicked", { source: "Modal" })}
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="grid max-[760px]:-mr-2 max-[760px]:-mt-2 size-11 min-[761px]:size-8 shrink-0 place-items-center rounded-[9px] text-fg-muted transition-colors hover:bg-hover"
                    >
                        <CloseIcon className="size-5 min-[761px]:size-4" />
                    </button>
                </div>
                {children && (
                    <div className="flex flex-1 flex-col gap-3 px-7 pb-6 pt-5">{children}</div>
                )}
                {footer && (
                    <div className="flex items-center justify-end gap-2 border-t border-line-soft bg-card-raised px-7 py-2.5">
                        {footer}
                    </div>
                )}
            </div>
        </Dialog>
    );
}

/** One option of a radio group, as a card: a white circle with a green rim and a gradient dot when picked. */
export function RadioCard({
    name,
    analyticsOption,
    checked,
    onChange,
    children,
}: {
    name: string;
    analyticsOption: string;
    checked: boolean;
    onChange: () => void;
    children: ReactNode;
}) {
    return (
        <label
            className={`relative flex cursor-pointer items-center gap-3 rounded-control border px-3 py-2.5 ${
                checked
                    ? "border-transparent bg-tint-green shadow-[0_0_0_2px_rgba(33,212,92,.55)]"
                    : "border-line bg-card"
            }`}
        >
            <input
                {...actionProps("skip_reason_selected", {
                    source: "Modal.RadioCard",
                    field: name,
                    option: analyticsOption,
                })}
                type="radio"
                name={name}
                checked={checked}
                onChange={onChange}
                className="peer sr-only"
            />
            <span
                className={`grid size-[22px] shrink-0 place-items-center rounded-full border-[1.5px] bg-card peer-focus-visible:outline-2 peer-focus-visible:outline-green ${
                    checked ? "border-green" : "border-line-control"
                }`}
                aria-hidden="true"
            >
                {checked && <span className="size-[11px] rounded-full bg-grad" />}
            </span>
            <span className="font-medium">{children}</span>
        </label>
    );
}
